import { useOutletContext } from "react-router-dom";
import { Target, Eye, MapPin, Users, Heart, Zap } from "lucide-react";

const HERO_IMAGE = "/assets/images/Sport_Eldorado_missie.webp";
const MISSION_IMAGE = "/assets/images/Sport_eldorado_Film.webp";
const VISION_IMAGE = "/assets/images/Sport_Eldorado_Visie.webp";

const values = [
  {
    icon: Heart,
    title: "Inclusiviteit",
    description: "Sport voor iedereen, zonder uitzonderingen",
    color: "from-red-500 to-pink-500"
  },
  {
    icon: Zap,
    title: "Empowerment",
    description: "Jongeren ontdekken hun volledige potentieel",
    color: "from-yellow-500 to-amber-500"
  },
  {
    icon: Users,
    title: "Gemeenschap",
    description: "Bruggen bouwen tussen wijken en culturen",
    color: "from-green-500 to-emerald-500"
  },
  {
    icon: MapPin,
    title: "Lokaal Impact",
    description: "Gent is ons speelveld en onze gemeenschap",
    color: "from-blue-500 to-cyan-500"
  }
];

export default function SportEldoradoMissie() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  return (
    <div className="min-h-screen" data-testid="sport-eldorado-missie-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <div className="text-center">
            <p className="text-[hsl(48,96%,53%)] font-bold text-lg mb-4 tracking-widest uppercase">Sport Eldorado</p>
            <h1 className="text-5xl md:text-7xl font-black text-white font-['Chivo'] mb-6">Missie & Visie</h1>
            <p className={`text-xl md:text-2xl max-w-2xl mx-auto ${isDark ? 'text-slate-200' : 'text-slate-400'}`}>
              Toegankelijke sport als katalysator voor verandering
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        
        {/* Mission Section - Full Width with Image */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Mission Content */}
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className={`p-3 rounded-xl ${isDark ? 'bg-gradient-to-br from-blue-500/30 to-blue-600/30' : 'bg-gradient-to-br from-blue-100 to-blue-200'}`}>
                  <Target className={`h-8 w-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <span className={`text-sm font-bold tracking-widest uppercase ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                  Onze Missie
                </span>
              </div>
              
              <h2 className={`text-4xl md:text-5xl font-black font-['Chivo'] mb-6 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Sport voor Elke Jongere in Gent
              </h2>
              
              <div className={`space-y-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <p className="text-lg leading-relaxed">
                  Sport Eldorado maakt sport <span className="font-bold text-amber-400">echt accessible</span> voor jongeren die het meest hulp nodig hebben. 
                  We elimineren barrières – financieel, cultureel, fysiek – zodat elke jongere in Gent kan spelen, groeien en schitteren.
                </p>
                <p className="leading-relaxed">
                  Via gratis sessions, workshops en evenementen in de buurt brengen we futsal rechtstreeks naar jongeren. 
                  Sport is het medium; transformation is het doel.
                </p>
                
                <div className="grid grid-cols-3 gap-4 pt-4">
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50 border border-slate-700' : 'bg-slate-100 border border-slate-200'}`}>
                    <div className={`text-2xl font-black font-['Chivo'] ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>0€</div>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Kosten</p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50 border border-slate-700' : 'bg-slate-100 border border-slate-200'}`}>
                    <div className={`text-2xl font-black font-['Chivo'] ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>100%</div>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Inclusief</p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50 border border-slate-700' : 'bg-slate-100 border border-slate-200'}`}>
                    <div className={`text-2xl font-black font-['Chivo'] ${isDark ? 'text-green-400' : 'text-green-600'}`}>5</div>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Wijken</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mission Image */}
            <div className="order-1 lg:order-2">
              <div className={`h-96 rounded-2xl overflow-hidden shadow-2xl ${isDark ? 'ring-1 ring-slate-700' : 'ring-1 ring-slate-200'}`}>
                <img 
                  src={MISSION_IMAGE} 
                  alt="Sport Eldorado activiteit" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 object-[50%_20%]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vision Section - Full Width with Image (Reversed) */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Vision Image */}
            <div>
              <div className={`h-96 rounded-2xl overflow-hidden shadow-2xl ${isDark ? 'ring-1 ring-slate-700' : 'ring-1 ring-slate-200'}`}>
                <img 
                  src={VISION_IMAGE} 
                  alt="Sport Eldorado toekomst" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>

            {/* Vision Content */}
            <div>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className={`p-3 rounded-xl ${isDark ? 'bg-gradient-to-br from-amber-500/30 to-amber-600/30' : 'bg-gradient-to-br from-amber-100 to-amber-200'}`}>
                  <Eye className={`h-8 w-8 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                </div>
                <span className={`text-sm font-bold tracking-widest uppercase ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                  Onze Visie
                </span>
              </div>
              
              <h2 className={`text-4xl md:text-5xl font-black font-['Chivo'] mb-6 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Een Gent Waar Iedereen Speelt
              </h2>
              
              <div className={`space-y-6 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                <p className="text-lg leading-relaxed">
                  We dromen van een <span className="font-bold text-amber-400">toekomst waarin sport geen privilege is</span>, 
                  maar een natuurlijk onderdeel van het opgroeien voor elke jongere in Gent.
                </p>
                <p className="leading-relaxed">
                  Waar futsal een brug bouwt tussen wijken, culturen en generaties. 
                  Waar jongeren niet alleen leren voetballen, maar ook leiderschap, discipline en gemeenschapszin.
                </p>
                
                <div className={`mt-8 p-6 rounded-xl border-2 ${isDark ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-200 bg-amber-50'}`}>
                  <p className={`italic text-lg ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                    "Door sport helpen we jongeren hun verhaal zelf te schrijven."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Values - Unique 2x2 Grid Layout */}
        <div className="mb-24">
          <h2 className={`text-4xl font-black font-['Chivo'] mb-12 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Wat Ons Drijft
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {values.map((value) => {
              const Icon = value.icon;
              const [fromColor, toColor] = value.color.split(" ");
              
              return (
                <div 
                  key={value.title}
                  className={`group relative overflow-hidden rounded-2xl p-8 min-h-64 flex flex-col justify-between transition-all duration-300 ${
                    isDark 
                      ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 hover:border-slate-600' 
                      : 'bg-white border border-slate-200 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {/* Gradient background effect */}
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 bg-gradient-to-br ${value.color}`} />
                  
                  {/* Content */}
                  <div className="relative z-10">
                    <div className={`inline-flex items-center justify-center h-14 w-14 rounded-xl mb-4 bg-gradient-to-br ${value.color} text-white shadow-lg`}>
                      <Icon className="h-7 w-7" />
                    </div>
                    
                    <h3 className={`text-2xl font-bold font-['Chivo'] mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {value.title}
                    </h3>
                    
                    <p className={`leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      {value.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Impact Stats - Unique Design */}
        <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-gradient-to-br from-blue-900/40 via-slate-900/40 to-amber-900/40 border border-slate-700' : 'bg-gradient-to-br from-blue-50 via-white to-amber-50 border border-slate-200 shadow-lg'}`}>
          <div className="p-12 md:p-16">
            <h2 className={`text-3xl md:text-4xl font-black font-['Chivo'] mb-12 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Ons Bereik & Impact
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { number: "100+", label: "Jongeren bereikt", icon: "👥" },
                { number: "5", label: "Actieve wijken", icon: "📍" },
                { number: "50+", label: "Wekelijkse sessies", icon: "⚽" },
                { number: "0€", label: "Toegang kosten", icon: "🎁" }
              ].map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-4xl mb-2">{stat.icon}</div>
                  <div className={`text-5xl font-black font-['Chivo'] mb-2 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`}>
                    {stat.number}
                  </div>
                  <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-24 text-center">
          <div className={`inline-block p-12 rounded-2xl max-w-2xl ${isDark ? 'bg-slate-900/50 border border-slate-700' : 'bg-slate-50 border border-slate-200'}`}>
            <h2 className={`text-3xl font-black font-['Chivo'] mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Sluit Je Bij Ons Aan
            </h2>
            <p className={`text-lg mb-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Sport Eldorado is voor elke jongere in Gent. Geen kosten, geen vragen, 100% inclusief.
            </p>
            <a 
              href="/contact" 
              className="inline-block px-8 py-3 bg-gradient-to-r from-[hsl(48,96%,53%)] to-amber-500 text-slate-900 font-bold rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Meer Informatie
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
